import {createPlayer} from './player';
import {Color, Texture, TextureLoader, SRGBColorSpace} from 'three';
import {AvatarChatClient} from './chat/avatarChatClient';
import type {ResponseConfig} from './chat/types';
import {AppSettings} from "./chat/types";
import {playPcmBase64} from "./audio";
import {BlendshapeAnimator} from "./blendshapeAnimator";
import {SequentialResponsePlayer} from "./sequentialResponsePlayer";

const viewportEl = document.getElementById('viewport') as HTMLElement;
const statusEl = document.getElementById('status') as HTMLElement | null;
const player = createPlayer(viewportEl, statusEl);

// Chat elements
const chatForm = document.getElementById('chatForm') as HTMLFormElement | null;
const chatInput = document.getElementById('chatInput') as HTMLInputElement | null;
const chatSend = document.getElementById('chatSend') as HTMLButtonElement | null;

let bgTexture: Texture | null = null;

// Chat over WebSocket
let chatClient: AvatarChatClient | null = null;
let defaultResponseCfg: ResponseConfig | undefined;
let chatReady = false;
// True from sending a question until the avatar has finished speaking the answer.
let answering = false;

// Blendshape animation
let blendshapeAnimator: BlendshapeAnimator | null = null;

// Sequential response playback
let responsePlayer: SequentialResponsePlayer | null = null;

if (chatForm && chatInput) {
    chatForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const msg = chatInput.value.trim();
        if (!msg) return;
        sendChat(msg);
        chatInput.value = '';
    });

    // Optional: clicking explicit send button triggers submit
    chatSend?.addEventListener('click', () => {
        chatForm.requestSubmit();
    });
}

// Resize
function onResize() {
    player.onResize();
}

window.addEventListener('resize', onResize);
onResize();

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
    if (bgTexture) {
        bgTexture.dispose();
        bgTexture = null;
    }
    if (blendshapeAnimator) {
        blendshapeAnimator.dispose();
        blendshapeAnimator = null;
    }
    if (responsePlayer) {
        responsePlayer.dispose();
        responsePlayer = null;
    }
    try {
        chatClient?.disconnect(1000, 'page unload');
    } catch {
    }
    player.dispose();
});

function setStatusMessage(message: string) {
    if (statusEl) statusEl.textContent = message;
}

function setSendLoading(loading: boolean) {
    if (!chatSend) return;
    if (loading) {
        chatSend.classList.add('loading');
        chatSend.disabled = true;
    } else {
        chatSend.classList.remove('loading');
        chatSend.disabled = false;
    }
}

// The gateway finishes sending well before the avatar finishes speaking; unlock only after playback.
async function finishAnswer(status: string) {
    await responsePlayer?.whenIdle();
    answering = false;
    setStatusMessage(status);
    setSendLoading(false);
}

function sendChat(message: string) {
    if (answering) {
        console.warn('[chat] question ignored while the previous answer is still playing:', message);
        setStatusMessage('Still answering');
        return;
    }
    if (!chatClient || !chatReady) {
        console.warn('[chat] not connected/session not ready');
        setStatusMessage('Chat not connected');
        return;
    }

    try {
        responsePlayer?.reset();
        responsePlayer?.unlockAudio();
        const handle = chatClient.sendText(message, defaultResponseCfg);
        answering = true;
        setSendLoading(true);
        setStatusMessage('Sending…');

        let answer = '';
        handle.onText((delta) => {
            answer += delta;
        });

        handle.onComplete(() => {
            console.log('[chat][final]', answer);
            setStatusMessage('Speaking…');
            void finishAnswer('Ready');
        });
    } catch (e) {
        console.error('[chat] send error', e);
        setStatusMessage('Send failed');
        setSendLoading(false);
    }
}

function handleChatResponse() {
    const nextMessageChunk = chatClient?.getNextMessageChunk();
    if (nextMessageChunk && responsePlayer) {
        console.log('[chat][response]', nextMessageChunk);
        responsePlayer.enqueueChunk(nextMessageChunk);
    }
}

async function applyBackgroundFromSettings(settings: { backgroundImage?: string; backgroundColor?: string; }) {
    // Dispose previous background texture if any
    if (bgTexture) {
        bgTexture.dispose();
        bgTexture = null;
    }

    const image = (settings.backgroundImage || '').trim();
    const color = (settings.backgroundColor || '').trim();

    if (image) {
        try {
            setStatusMessage('Loading background…');
            const loader = new TextureLoader();
            const tex = await loader.loadAsync(image);
            // Use sRGB for correct color if applicable
            (tex as any).colorSpace = SRGBColorSpace as any;
            player.scene.background = tex;
            bgTexture = tex;
            setStatusMessage('Background loaded');
            return;
        } catch (err) {
            console.warn('Failed to load background image:', err);
            setStatusMessage('Background image failed; using color/default');
        }
    }

    if (color) {
        try {
            player.scene.background = new Color(color);
        } catch {
        }
    }
}

async function loadFromSettings() {
    try {
        setStatusMessage('Loading settings…');
        let res = await fetch('settings.json');
        if (!res || !res.ok) {
            setStatusMessage('Settings not found; no model loaded.');
            return;
        }
        const settings: AppSettings = await res.json();

        await applyBackgroundFromSettings(settings);

        defaultResponseCfg = settings.response;
        const gatewayUrl = (settings.gatewayUrl || '').trim();
        const avatarKey = (settings.avatarKey || '').trim();

        try {
            chatClient = new AvatarChatClient({
                gatewayUrl,
                avatarKey,
                onOpen: () => {
                    setStatusMessage('Connected');
                    console.info('[chat][status] connected');
                },
                onClose: () => {
                    chatReady = false;
                    setStatusMessage('Disconnected');
                    console.info('[chat][status] disconnected');
                },
                onSocketError: () => {
                    setStatusMessage('Connection error');
                    console.warn('[chat][error] connection error');
                },
                onErrorMessage: (err) => {
                    console.warn('[chat][error]', err);
                    void finishAnswer(err.message || 'Chat error');
                },
            });

            chatClient.messageQueueUpdated = handleChatResponse;

            if (gatewayUrl && avatarKey) {
                try {
                    await chatClient.connect();
                    chatReady = true;
                    setStatusMessage('Ready');
                } catch (e) {
                    console.warn('[chat][init] session start failed', e);
                    setStatusMessage('Session start failed');
                }
            } else {
                setStatusMessage('No gatewayUrl or avatarKey; chat disabled');
            }
        } catch (e) {
            console.warn('Chat client init failed:', e);
        }

        const modelPath = (settings?.modelPath || '').trim();
        if (!modelPath) {
            setStatusMessage('No modelPath in settings; nothing to load.');
            return;
        }
        await player.loadFromURL(modelPath);

        // Initialize blendshape animator AFTER model is loaded
        blendshapeAnimator = new BlendshapeAnimator(player.scene);
        blendshapeAnimator.setKnipperAction(player.knipperAction);

        // Initialize sequential response player with animator reference
        responsePlayer = new SequentialResponsePlayer(blendshapeAnimator);

        setStatusMessage('Ready');
    } catch (err) {
        console.error('Failed to load settings:', err);
        setStatusMessage('Invalid settings; no model loaded.');
    }
}

loadFromSettings().finally(() => document.getElementById('modelLoader')?.remove());
